import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { MdVideoLibrary } from "react-icons/md";
import { MdLanguage } from "react-icons/md";
import { MdCheckCircle } from "react-icons/md";
import { MdOutlinePlaylistPlay } from "react-icons/md";
import { useDispatch, useSelector } from "react-redux";
import { fetchEducationalVideos } from "../store/reducers/videoSectionSlice";
import Player from "@vimeo/player";
import { toast } from "react-toastify";

import {
  submitVideoQuiz,
  resetVideoQuizState,
} from "../store/reducers/quizzesSlice";
import QuizAlertModal from "./components/QuizAlertModal";
import { setQuizAlertModal } from "../store/reducers/modalSlice";
import { toWords } from "number-to-words";

const VIDEO_VIEWPORT_STYLE = {
  aspectRatio: "21 / 9",
  overflow: "hidden",
  position: "relative",
};
const VIDEO_IFRAME_STYLE = {
  border: 0,
  display: "block",
  height: "100%",
  inset: 0,
  position: "absolute",
  width: "100%",
};

const EducationalVideos = () => {
  const [t] = useTranslation();
  const dispatch = useDispatch();

  const { organizationDetails } = useSelector((state) => state.organization);
  const { educationalVideos, loading } = useSelector(
    (state) => state.eduVideos || {},
  );

  const { videoQuiz } = useSelector((state) => state.quizzes || {});
  const { quizAlertModal } = useSelector((state) => state.modal || {});

  const [selectedVideo, setSelectedVideo] = useState(null);
  // "dk" = Danish, "en" = English
  const [activeLang, setActiveLang] = useState("dk");
  const [watchedVideos, setWatchedVideos] = useState({});
  const [visibleCount, setVisibleCount] = useState(2);
  const frameColor = "#3F3F3F";

  useEffect(() => {
    if (organizationDetails?.id) {
      dispatch(fetchEducationalVideos(organizationDetails.id));
    }
  }, [dispatch, organizationDetails?.id]);

  /* ── Embed URL helper ── */
  const getEmbedUrl = (url) => {
    if (!url) return "";
    if (url.includes("youtube.com/watch")) {
      return url.replace("watch?v=", "embed/") + "?enablejsapi=1";
    }
    if (url.includes("vimeo.com")) {
      if (url.includes("player.vimeo.com")) return url;
      const videoId = url.split("/").pop();
      return `https://player.vimeo.com/video/${videoId}`;
    }
    return url;
  };

  const getVideoWatchKey = (id, lang) => `${id}-${lang}`;

  /* ── Vimeo "ended" listener ── */
  useEffect(() => {
    if (!educationalVideos?.length) return;

    const handlers = [];

    educationalVideos.forEach((item) => {
      ["dk", "en"].forEach((lang) => {
        const iframeId = `video-${item.id}-${lang}`;
        const iframe = document.getElementById(iframeId);
        if (!iframe) return;

        try {
          const player = new Player(iframe);
          const handleEnded = () => {
            setWatchedVideos((prev) => ({
              ...prev,
              [getVideoWatchKey(item.id, lang)]: true,
            }));
          };
          player.on("ended", handleEnded);
          handlers.push({ player, handleEnded });
        } catch (err) {
          console.log("Player init error", err);
        }
      });
    });

    return () => {
      handlers.forEach(({ player, handleEnded }) =>
        player.off("ended", handleEnded),
      );
    };
  }, [educationalVideos, activeLang, visibleCount]);

  useEffect(() => {
    setVisibleCount(2);
  }, [activeLang]);

  /* ── Mark as Complete (same flow as video-end) ── */
  const handleMarkAsComplete = (item, langKey) => {
    if (!watchedVideos[getVideoWatchKey(item.id, langKey)]) {
      toast.info("Please finish watching the video before marking it complete.");
      return;
    }

    setSelectedVideo(item);
    dispatch(setQuizAlertModal(true));
  };

  /* ── Quiz modal: Yes ── */
  const handleYes = async () => {
    if (!selectedVideo) return;

    const payload = {
      video_id: selectedVideo?.id,
      organization_id: organizationDetails?.id,
      want_video_quiz: true,
    };

    try {
      await dispatch(submitVideoQuiz(payload)).unwrap();

      toast.success("Quiz request submitted successfully!", {
        onClose: () => {
          dispatch(resetVideoQuizState());
        },
      });

      dispatch(setQuizAlertModal(false));
      setSelectedVideo(null);
    } catch (error) {
      toast.error(error?.error || error?.message || "Something went wrong");
    }
  };

  /* ── Quiz modal: No ── */
  const handleNo = () => {
    setSelectedVideo(null);
    dispatch(setQuizAlertModal(false));
  };

  const num = organizationDetails?.videos_questions_per_quiz || 3;
  const number_of_quiz = toWords(num);

  /* ── Active videos for current language ── */
  const isDanish = activeLang === "dk";
  const activeVideos = (educationalVideos || [])
    .filter((item) => item.active)
    .filter((item) => {
      const link = isDanish ? item.link_dk : item.link;
      return !!link;
    });
  const visibleVideos = activeVideos.slice(0, visibleCount);
  const upNextVideos = activeVideos.slice(visibleCount, visibleCount + 3);
  const remainingVideos = Math.max(activeVideos.length - visibleCount, 0);

  /* ── Render a single video card ── */
  const renderVideoCard = (item) => {
    const title = isDanish ? item.name_dk : item.name;
    const link = isDanish ? item.link_dk : item.link;
    const langKey = isDanish ? "dk" : "en";
    const canMarkComplete = Boolean(
      watchedVideos[getVideoWatchKey(item.id, langKey)],
    );

    return (
      <div
        key={`${item.id}-${langKey}`}
        className="mb-7 overflow-hidden rounded-xl p-4 shadow-[0_18px_42px_rgba(31,41,55,0.18)] ring-1 ring-black/10 sm:p-5"
        style={{ backgroundColor: frameColor }}
      >
        <div
          className="rounded-lg bg-black shadow-inner"
          style={VIDEO_VIEWPORT_STYLE}
        >
          <iframe
            id={`video-${item.id}-${langKey}`}
            src={getEmbedUrl(link)}
            frameBorder="0"
            allow="autoplay; fullscreen; picture-in-picture"
            allowFullScreen
            className="block"
            style={VIDEO_IFRAME_STYLE}
            title={title}
          />
        </div>

        <div
          className="mt-4 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between"
          style={{ backgroundColor: frameColor }}
        >
          <div className="min-w-0">
            <p className="truncate text-base font-bold leading-snug text-white">
              {title}
            </p>
            {item.tag && (
              <p className="mt-0.5 text-sm text-white">Tag: {item.tag}</p>
            )}
          </div>

          <button
            onClick={() => handleMarkAsComplete(item, langKey)}
            disabled={!canMarkComplete}
            className="inline-flex min-h-10 items-center justify-center gap-2 rounded-md border-0 px-4 py-2 text-sm font-semibold text-white shadow-lg transition-colors outline-none focus:outline-none focus:ring-0 sm:min-w-[168px]"
            style={{
              backgroundColor: canMarkComplete ? "#E05A50" : "#B4B2A9",
              cursor: canMarkComplete ? "pointer" : "not-allowed",
              opacity: canMarkComplete ? 1 : 0.75,
            }}
          >
            <MdCheckCircle size={18} />
            Mark as Complete
          </button>
        </div>
      </div>
    );
  };

  const renderUpNext = () => {
    if (!upNextVideos.length && !remainingVideos) return null;

    return (
      <aside className="rounded-xl bg-white p-4 shadow-sm ring-1 ring-gray-200 lg:sticky lg:top-4">
        <div className="mb-4 flex items-center gap-2 text-sm font-bold text-[#3F3F3F]">
          <MdOutlinePlaylistPlay className="text-xl text-[#FF5A5F]" />
          Up Next
        </div>

        <div className="space-y-3">
          {upNextVideos.map((item, index) => {
            const videoIndex = visibleCount + index;
            const title = isDanish ? item.name_dk : item.name;
            const link = isDanish ? item.link_dk : item.link;

            return (
              <button
                key={`up-next-${item.id}`}
                type="button"
                onClick={() => setVisibleCount(videoIndex + 1)}
                className="flex w-full items-center gap-3 border-0 bg-transparent p-0 text-left"
              >
                <span
                  className="block h-14 w-20 shrink-0 overflow-hidden rounded-md"
                  style={{ backgroundColor: frameColor }}
                >
                  <iframe
                    src={getEmbedUrl(link)}
                    title={`Up next ${title}`}
                    className="h-full w-full pointer-events-none"
                    frameBorder="0"
                  />
                </span>
                <span className="min-w-0">
                  <span className="block truncate text-[13px] font-bold text-gray-900">
                    {title}
                  </span>
                  <span className="block text-[11px] text-gray-500">
                    Introduction course
                  </span>
                </span>
              </button>
            );
          })}
        </div>

        {remainingVideos > 0 && (
          <button
            type="button"
            onClick={() =>
              setVisibleCount((count) => Math.min(activeVideos.length, count + 5))
            }
            className="mt-5 inline-flex min-h-11 w-full items-center justify-center rounded-md border-0 bg-[#FF5A5F] px-3 py-1 text-[14px] text-white transition-colors hover:bg-[#F04F54]"
          >
            Watch {remainingVideos} More Video
          </button>
        )}
      </aside>
    );
  };

  return (
    <div className="min-h-[93vh] overflow-auto bg-white p-3 text-[#20232A] sm:p-4">
      <div className="flex items-center justify-between mb-2">
        <h1 className="mb-4 text-[24px] font-bold leading-tight text-black md:text-[30px]">
          {t("userMenuSidebar.label.Educational_video")}
        </h1>

        <div className="flex items-center gap-1 sm:gap-2">
          <button
            onClick={() => setActiveLang("dk")}
            className={`px-3 sm:px-4 py-1.5 rounded-md text-sm font-semibold transition-all
    focus:outline-none focus:ring-0 active:outline-none active:ring-0
    ${
      activeLang === "dk"
        ? "text-white border border-transparent shadow-none"
        : "text-gray-600 bg-white border border-gray-200 hover:bg-gray-50"
    }`}
            style={activeLang === "dk" ? { backgroundColor: "#E05A50" } : {}}
          >
            {t("IntroductionVideo.label.Danish") || "Danish"}
          </button>

          <button
            onClick={() => setActiveLang("en")}
            className={`flex items-center gap-1 px-3 sm:px-4 py-1.5 rounded-md text-sm font-semibold transition-all
    focus:outline-none focus:ring-0 active:outline-none active:ring-0
    ${
      activeLang === "en"
        ? "text-white border border-transparent shadow-none"
        : "text-gray-600 bg-white border border-gray-200 hover:bg-gray-50"
    }`}
            style={activeLang === "en" ? { backgroundColor: "#E05A50" } : {}}
          >
            <MdLanguage size={16} />
            {t("IntroductionVideo.label.English") || "English"}
          </button>
        </div>
      </div>

      {/* ── Video list ── */}
      {loading ? (
        <div className="flex justify-center items-center py-16">
          <div className="text-center">
            <div
              className="inline-block w-8 h-8 border-4 border-t-transparent rounded-full animate-spin mb-3"
              style={{ borderColor: "#E05A50", borderTopColor: "transparent" }}
            />
            <p className="text-gray-500 text-sm">Loading videos…</p>
          </div>
        </div>
      ) : activeVideos.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-16 text-gray-400">
          <MdVideoLibrary size={48} className="mb-3 opacity-40" />
          <p className="text-sm">No videos available for this language.</p>
        </div>
      ) : (
        <div className="grid gap-6 lg:grid-cols-[minmax(0,1fr)_280px]">
          <div>{visibleVideos.map((item) => renderVideoCard(item))}</div>
          {renderUpNext()}
        </div>
      )}

      {/* ── Quiz Modal ── */}
      {quizAlertModal && (
        <QuizAlertModal
          description={`You have watched an educational video. Do you want to increase your score by answering ${number_of_quiz} quick questions related to the video?`}
          subText={`Questions will be sent after a ${organizationDetails?.delay_videos_quiz} hours.`}
          open={quizAlertModal}
          loading={videoQuiz?.loading}
          onClose={() => dispatch(setQuizAlertModal(false))}
          onNo={handleNo}
          onYes={handleYes}
        />
      )}
    </div>
  );
};

export default EducationalVideos;
